import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, X, ChevronDown, CheckCircle2, Flame } from "lucide-react";

/**
 * TermsModal — Modal de T&C con scroll obligatorio para activar el botón
 *
 * Props:
 *  - isOpen       {boolean}   — controla visibilidad
 *  - onAccept     {function}  — llamado cuando el usuario acepta (recibe { acceptedAt })
 *  - onDecline    {function}  — llamado cuando cancela
 *  - terms        {string}    — texto completo de los T&C
 *  - eventTitle   {string}    — nombre del evento / contexto
 *  - accentColor  {string}    — "orange" (BurgerFest) | "yellow" (default supernova)
 *  - submitLabel  {string}    — texto del botón de aceptar
 */
export default function TermsModal({
  isOpen,
  onAccept,
  onDecline,
  terms = "",
  eventTitle = "Burger Fest",
  accentColor = "orange",
  submitLabel  = "Acepto y completo mi registro",
}) {
  const [scrolled, setScrolled]   = useState(false);
  const [progress, setProgress]  = useState(0);
  const scrollRef = useRef(null);

  // Resetear estado cada vez que se abre el modal
  useEffect(() => {
    if (isOpen) {
      setScrolled(false);
      setProgress(0);
      // Pequeño delay para que el DOM esté listo
      setTimeout(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Si el contenido no requiere scroll, habilitamos directo
        if (el.scrollHeight <= el.clientHeight + 10) {
          setScrolled(true);
          setProgress(100);
        }
      }, 120);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const el = e.target;
    const total = el.scrollHeight - el.clientHeight;
    const pct = total > 0 ? Math.min(100, Math.round((el.scrollTop / total) * 100)) : 100;
    setProgress(pct);
    if (pct >= 95) setScrolled(true);
  };

  const handleAccept = () => {
    if (!scrolled) return;
    onAccept({ acceptedAt: new Date().toISOString() });
  };

  // Colores según tema
  const isOrange = accentColor === "orange";
  const activeBtnClass = isOrange
    ? "bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg shadow-orange-500/30"
    : "bg-supernova-500 hover:bg-supernova-600 text-white shadow-lg";
  const progressColor = isOrange ? "#f97316" : "#f5c518";
  const accentText    = isOrange ? "text-orange-600" : "text-supernova-600";
  const accentBg      = isOrange ? "bg-orange-100"   : "bg-supernova-100";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Overlay */}
          <motion.div
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />

          {/* Panel */}
          <motion.div
            className="relative z-10 w-full max-w-lg bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            style={{ maxHeight: "88vh" }}
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${accentBg}`}>
                <FileText size={20} className={accentText} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-black text-gray-900 text-base leading-tight">Términos y Condiciones</h2>
                <p className="text-xs text-gray-400 truncate">{eventTitle}</p>
              </div>
              <button onClick={onDecline}
                className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition flex-shrink-0">
                <X size={18} />
              </button>
            </div>

            {/* Barra de progreso de lectura */}
            <div className="h-1 bg-gray-100 flex-shrink-0">
              <motion.div
                className="h-full rounded-full"
                style={{ background: progressColor }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.15 }}
              />
            </div>

            {/* Aviso de scroll (solo si no ha llegado al fondo) */}
            <AnimatePresence>
              {!scrolled && (
                <motion.div
                  className="flex items-center gap-2 justify-center py-2 flex-shrink-0"
                  style={{ background: isOrange ? "rgba(255,165,0,0.08)" : "rgba(245,197,24,0.08)" }}
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <ChevronDown size={13} className="animate-bounce" style={{ color: progressColor }} />
                  <p className="text-xs font-semibold" style={{ color: progressColor }}>
                    Lee el documento completo para poder registrarte
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Cuerpo scrolleable */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-5 py-4"
              style={{ minHeight: 0 }}
              onScroll={handleScroll}
            >
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed select-text">
                {terms || "No se han configurado términos para este evento."}
              </pre>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0"
              style={{ background: "rgba(249,250,251,1)" }}>

              {/* Confirmación visual cuando llegó al fondo */}
              <AnimatePresence>
                {scrolled && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 mb-3"
                  >
                    <CheckCircle2 size={15} className="text-green-500 flex-shrink-0" />
                    <p className="text-xs text-green-700 font-semibold">
                      Has leído los términos y condiciones completos
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onDecline}
                  className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-100 transition"
                >
                  Cancelar
                </button>
                <motion.button
                  type="button"
                  onClick={handleAccept}
                  disabled={!scrolled}
                  whileTap={scrolled ? { scale: 0.97 } : {}}
                  className={`flex-1 py-3 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all ${
                    scrolled ? activeBtnClass : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {scrolled
                    ? <><Flame size={15} /> {submitLabel}</>
                    : <>Desplázate para continuar</>
                  }
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
